import Page from "../../layout/Page";
import PageHeader from "../../layout/PageHeader";
import PageContent from "../../layout/PageContent";
import {FlexList} from "../../components/flexlist/FlexList";
import {EnumSystemMessageAudience, EnumSystemMessageMessageType, FilterFindManySystemMessageInput, SortFindManySystemMessageInput, SystemMessagesQuery, useSystemMessageAcknowledgeMutation, useSystemMessagesLazyQuery} from "../../Queries";
import {FlexCommand, FlexFilter, FlexListColumn, FlexListRequestData} from "../../components/flexlist/FlexTypes";
import dayjs from "dayjs";
import {Box, Chip, Typography} from "@mui/material";
import {CheckCircle} from "@mui/icons-material";
import React, {useState} from "react";
import {useRecoilValue} from "recoil";
import {CurrentUserIsAdmin, CurrentUserState} from "../../atoms/CurrentUser";

export const SystemMessagesPage = () => {
   const [filters] = useState<FlexFilter<FilterFindManySystemMessageInput>[]>([
      {
         id: 'audience',
         label: 'Audience',
         options: [
            {
               label: 'System Administrators',
               filter: {audience: EnumSystemMessageAudience.SYSADM}
            },
            {
               label: 'Administrators',
               filter: {audience: EnumSystemMessageAudience.ADMIN}
            },
            {
               label: 'Brand House',
               filter: {audience: EnumSystemMessageAudience.BRAND}
            }
         ]
      },
      {
         id: 'messageType',
         label: 'Message Type',
         options: [
            {
               label: 'Information',
               filter: {messageType: EnumSystemMessageMessageType.INFORMATION}
            },
            {
               label: 'Warning',
               filter: {messageType: EnumSystemMessageMessageType.WARNING}
            },
            {
               label: 'Critical',
               filter: {messageType: EnumSystemMessageMessageType.CRITICAL}
            }
         ]
      },
      {
         id: 'status',
         label: 'Status',
         options: [
            {
               label: 'Unacknowledged',
               filter: {acknowledgedTs: null}
            }
         ]
      }
   ]);

   const currentUser = useRecoilValue(CurrentUserState);
   const isAdmin = useRecoilValue(CurrentUserIsAdmin);

   const [requestData] = useSystemMessagesLazyQuery({
      fetchPolicy: "no-cache"
   });

   const [acknowledgeMessage] = useSystemMessageAcknowledgeMutation();

   type SystemMessageListItem = SystemMessagesQuery["SystemMessages"][number];
   type SystemMessageEntry = NonNullable<SystemMessageListItem["entries"][number]>;

   const getEntries = (message: SystemMessageListItem): SystemMessageEntry[] => (
      message.entries.filter((entry): entry is SystemMessageEntry => Boolean(entry))
   );

   const columns: FlexListColumn<SystemMessageListItem>[] = [
      {
         id: "lastActivityTs",
         label: "Last activity",
         width: 20,
         get: (row) => row.lastActivityTs,
         render: (data) => {
            const momentInTime = dayjs(data);
            return <Box>
               {momentInTime.format('HH:mm:ss, D. MMM YYYY')}
            </Box>
         }
      },
      {
         id: "subject",
         label: "Subject",
         field: "subject",
         width: 25,
      },
      {
         id: "entries",
         label: "Entries",
         width: 45,
         get: (row) => getEntries(row),
         render: (data: SystemMessageEntry[], row) => {
            const entries = data || [];
            return <Box>
               {entries.map((entry, index) => (
                  <Box key={`${row?._id}-${entry.timestamp}-${index}`} mb={index === entries.length - 1 ? 0 : 1}>
                     <Typography
                        variant="body2"
                        component="pre"
                        sx={{
                           m: 0,
                           fontFamily: 'inherit',
                           whiteSpace: 'pre-wrap',
                           wordBreak: 'break-word'
                        }}
                     >
                        {entry.text}
                     </Typography>
                     <Typography variant="caption" color="text.secondary">
                        {dayjs(entry.timestamp).format('HH:mm:ss, D. MMM YYYY')}
                     </Typography>
                  </Box>
               ))}
            </Box>
         }
      },
      {
         id: "acknowledged",
         label: "Status",
         width: 30,
         get: (row) => row.acknowledgedTs,
         render: (data) => {
            if (data) {
               return <Chip
                  size="small"
                  color="success"
                  icon={<CheckCircle/>}
                  label={`Acknowledged ${dayjs(data).format('D. MMM')}`}
               />
            }
            return <Chip size="small" color="warning" label="Unacknowledged"/>
         }
      }
   ];

   const commands: FlexCommand<SystemMessageListItem>[] = [
      {
         id: 'acknowledge',
         title: 'Acknowledge selected messages',
         icon: CheckCircle,
         execute: async (rows) => {
            for (const message of rows) {
               if (!message.acknowledgedTs) {
                  await acknowledgeMessage({
                     variables: {
                        filter: {_id: message._id},
                        record: {
                           acknowledgedTs: new Date()
                        }
                     }
                  });
               }
            }
         }
      }
   ];

   // Filter messages client-side based on user's audience
   const isMessageRelevantToUser = (message: SystemMessageListItem) => {
      // System administrators see all messages
      if (isAdmin) return true;

      // Check if message is for administrator and user is admin
      if (message.audience === EnumSystemMessageAudience.ADMIN && isAdmin) {
         return true;
      }

      // Check if message is for brandhouse and user has brandhouse access
      if (message.audience === EnumSystemMessageAudience.BRAND && currentUser?.brandHouseId) {
         // If audienceBrandId is specified, check if it matches user's brandhouse
         return !message.audienceBrandId || message.audienceBrandId === currentUser.brandHouseId;
      }

      // System admin messages are only for system administrators
      if (message.audience === EnumSystemMessageAudience.SYSADM && isAdmin) {
         return true;
      }

      return false;
   };

   const onRequestData = async (search: string | undefined, sort: string | undefined, page: number, filter?: FilterFindManySystemMessageInput, maxItems?: number): Promise<FlexListRequestData<SystemMessageListItem>> => {
      const {data} = await requestData({
         variables: {
            limit: maxItems || 50,
            sort: (sort as SortFindManySystemMessageInput) || SortFindManySystemMessageInput.LASTACTIVITYTS_DESC,
            filter: filter
         }
      });

      // Filter messages based on user's audience access
      const allMessages = data?.SystemMessages || [];
      const resultData = allMessages.filter(isMessageRelevantToUser);

      return {
         data: resultData,
         totalCount: resultData.length
      }
   }

   return <Page>
      <PageHeader title={"System Messages"}/>
      <PageContent>
         <FlexList
            globalId={"systemMessagesList"}
            columns={columns}
            itemsPerPage={50}
            filters={filters}
            selections={true}
            commands={commands}
            onRequestData={onRequestData}
         />
      </PageContent>
   </Page>
}
